/**
 * Draggable layer for Odie-placed objects (text, images, signatures).
 *
 * Dragging is optimistic: the box follows the pointer locally at 60fps and
 * only commits to the server on release. That keeps the interaction smooth
 * without issuing a request per mouse-move, and because a commit only
 * regenerates Odie's overlay stream, it can never disturb the original page.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { PlacedObject } from '../api'
import { unrotateDelta } from './rotation'

type Handle = 'move' | 'se'

interface Props {
  objects: PlacedObject[]
  /** Let clicks through to the page: another tool is placing something. */
  passive?: boolean
  /** The page's /Rotate; drags arrive in screen axes. */
  rotation?: number
  pageHeight: number
  originX: number
  originY: number
  scale: number
  selectedId: string | null
  onSelect: (id: string | null) => void
  onCommit: (id: string, patch: { x?: number; y?: number; width?: number; height?: number }) => void
  onEditText: (object: PlacedObject) => void
  onDelete: (id: string) => void
}

interface DragState {
  id: string
  handle: Handle
  startX: number
  startY: number
  origin: { x: number; y: number; width: number; height: number }
}

export function ObjectLayer({
  objects,
  passive = false,
  rotation = 0,
  pageHeight,
  originX,
  originY,
  scale,
  selectedId,
  onSelect,
  onCommit,
  onEditText,
  onDelete,
}: Props) {
  const [drag, setDrag] = useState<DragState | null>(null)
  // Local override while dragging, so we do not wait on the server per frame.
  const [ghost, setGhost] = useState<Record<string, Partial<PlacedObject>>>({})
  const ghostRef = useRef(ghost)
  ghostRef.current = ghost
  const dragRef = useRef<DragState | null>(null)
  dragRef.current = drag

  const begin = (event: React.PointerEvent, object: PlacedObject, handle: Handle) => {
    event.preventDefault()
    event.stopPropagation()
    ;(event.target as Element).setPointerCapture?.(event.pointerId)
    onSelect(object.id)
    setDrag({
      id: object.id,
      handle,
      startX: event.clientX,
      startY: event.clientY,
      origin: {
        x: object.x,
        y: object.y,
        width: object.width,
        height: object.height,
      },
    })
  }

  const move = useCallback(
    (event: PointerEvent) => {
      const state = dragRef.current
      if (!state) return

      // Screen pixels -> the page's own axes -> PDF points. Screen y grows
      // downward, PDF y upward.
      const turned = unrotateDelta(rotation, event.clientX - state.startX, event.clientY - state.startY)
      const dx = turned.x / scale
      const dy = -turned.y / scale

      setGhost((previous) => ({
        ...previous,
        [state.id]:
          state.handle === 'move'
            ? { x: state.origin.x + dx, y: state.origin.y + dy }
            : {
                // Resizing from the bottom-right keeps the top-left anchored,
                // so y must move with the height change.
                width: Math.max(8, state.origin.width + dx),
                height: Math.max(8, state.origin.height - dy),
                y: state.origin.y + dy,
              },
      }))
    },
    [scale, rotation],
  )

  const end = useCallback(() => {
    const state = dragRef.current
    if (!state) return
    dragRef.current = null
    setDrag(null)

    // Read the drop position from the ref, not inside a state updater: React
    // may run an updater twice, and the move was being saved twice.
    const patch = ghostRef.current[state.id]
    if (patch) {
      // Only send what actually changed: a move has no size in it.
      const changes = Object.fromEntries(
        (['x', 'y', 'width', 'height'] as const)
          .filter((key) => patch[key] !== undefined)
          .map((key) => [key, patch[key] as number]),
      )
      onCommit(state.id, changes)
    }
    setGhost((previous) => {
      const next = { ...previous }
      delete next[state.id]
      return next
    })
  }, [onCommit])

  useEffect(() => {
    if (!drag) return
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', end)
    }
  }, [drag, move, end])

  // Delete removes the selected object, matching every other editor.
  useEffect(() => {
    if (!selectedId) return
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.isContentEditable || target?.tagName === 'INPUT') return
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault()
        onDelete(selectedId)
      } else if (event.key === 'Escape') {
        onSelect(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId, onDelete, onSelect])

  return (
    <div className="objects" data-passive={passive || undefined}>
      {objects.map((raw) => {
        const object = { ...raw, ...ghost[raw.id] } as PlacedObject
        const selected = selectedId === object.id

        // A text object's y is its baseline; an image's y is its bottom edge.
        const boxHeight = object.height || object.fontSize * 1.2
        const boxWidth = object.width || Math.max(object.text.length * object.fontSize * 0.5, 40)
        // Object coordinates are absolute user space, like runs.
        const localY = object.y - originY
        const top =
          object.kind === 'image' || object.hasImage
            ? (pageHeight - localY - boxHeight) * scale
            : (pageHeight - localY - object.fontSize) * scale

        return (
          <div
            key={object.id}
            className="obj"
            data-selected={selected}
            data-kind={object.kind}
            style={{
              left: (object.x - originX) * scale,
              top,
              width: boxWidth * scale,
              height: boxHeight * scale,
              transform: object.rotation ? `rotate(${-object.rotation}deg)` : undefined,
            }}
            onPointerDown={(e) => begin(e, object, 'move')}
            onDoubleClick={() => object.kind !== 'image' && onEditText(object)}
            title={
              object.kind === 'text'
                ? 'Drag to move · double-click to edit · Delete to remove'
                : 'Drag to move · Delete to remove'
            }
          >
            {selected && (
              <span
                className="obj__handle"
                onPointerDown={(e) => begin(e, object, 'se')}
                title="Resize"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
